/**
 * PrescriptionViewWithPrintCSS
 *
 * Renders the prescription preview with all print-related CSS.
 * Extracted from PrescriptionMakingPage/index.jsx for maintainability.
 */
import React from 'react'
import MenuBook from '@mui/icons-material/MenuBook'
import { Box, Button, Switch, Typography } from '@mui/material'
import PrescriptionViewComponent from './PrescriptionViewComponent'
import A4PrintSettings from './printSettings/A4PrintSettings'
import { A4DpiToDetailsMap } from 'src/consumer/constants'
import {
  PMPPrintWrapper,
  PMPSectionHeading,
} from '../PrescriptionMakingPageStyles'

export default function PrescriptionViewWithPrintCSS({
  showInUI,
  // State values
  allVitalsOfThisPatient,
  bookedEventInfo,
  colorMode,
  drugsAdvice,
  emrSections,
  emrSectionsToShowInOrder,
  finalRequestedTestsBy,
  freeFormRichText,
  jointHomunculusData,
  nextVisitInputType,
  nextVisitInputValue,
  patientHistoryNotes,
  prescriptionData,
  prescriptionViewSettings,
  printSettings,
  referredToDoctorName,
  selectedAdvice,
  selectedAssessments,
  selectedComplaints,
  selectedDiagnosis,
  selectedInstructions,
  selectedInvestigations,
  selectedObservations,
  selectedTherapies,
  suggestedBookingTherapy,
  // State setters
  setTotalPages,
  setPrintSettings,
  setPrescriptionData,
  setValue,
  setPropValue,
  // Computed/derived values
  DEBUG_MODE,
  isPrintingPastHistoryReports,
  printType,
  honorPropValue,
  value,
  handleColorModeChange,
}) {
  // Get A4 dimensions for print CSS from constants (authoritative values)
  // Use A4DpiToDetailsMap values instead of calculating to ensure correct aspect ratio
  const currentDpiData = A4DpiToDetailsMap?.[printSettings?.dpi] || A4DpiToDetailsMap[96]
  const heightOfA4Page = currentDpiData?.height || 1123
  const widthOfA4Page = currentDpiData?.width || 794
  // Printed page height must fit WITHIN the physical A4 printable area, which at 96dpi is
  // 297mm = 1122.52px — i.e. ~0.5px SHORTER than A4DpiToDetailsMap.height (1123px). Pinning a
  // page/content box to the full 1123px overflows the sheet by a sub-pixel, and WebKit/Safari
  // rounds that overflow UP onto a trailing blank/"seeped" sheet (Chrome rounds it away). Clamp
  // every print height pin 1px under the nominal A4 height so each .prescription-page maps to
  // exactly one physical sheet across browsers. Matches the long-standing clone-path fix in
  // hooks/usePrintActions.jsx (heightOfA4Page - 1).
  const printPageHeight = heightOfA4Page - 1

  // Safari/WebKit rounds the printable A4 content area down ~1px more aggressively than Chrome,
  // so even the clamped printPageHeight page spills onto a trailing sheet at 100% scale (Chrome
  // tolerates the sub-pixel slack; Safari doesn't). A larger height reduction would break FULL
  // pages, whose content is laid out to fill the page — so instead we mirror the manual "print at
  // 99%" workaround with a Safari-only sub-100% zoom that scales container AND content together.
  const _ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const isSafari = /safari/i.test(_ua) && !/chrome|chromium|crios|fxios|edg|android/i.test(_ua)

  return (
    <>
      <style>
        {`
          @media print {
            /* Hide all UI elements, buttons, and debug labels */
            /* These rules apply unconditionally to ensure UI is hidden */
            .hide-on-print,
            .hide-on-print-only,
            button,
            [class*="MuiButton"],
            [class*="MuiIconButton"],
            [class*="MuiToggleButton"],
            [class*="MuiToggleButtonGroup"],
            [class*="MuiBackdrop"],
            [class*="MuiDrawer"]:not([class*="print"]),
            [class*="MuiDialog"],
            [class*="MuiModal"],
            input[type="button"],
            input[type="submit"],
            a[role="button"],
            /* Hide view toggle and dimension controls */
            [class*="MuiBox-root"]:has([class*="MuiToggleButtonGroup"]),
            [class*="MuiBox-root"]:has(button[class*="MuiButton"]),
            /* Hide debug labels and floating elements */
            div[style*="position: absolute"][style*="right"]:not([class*="print"]),
            div[style*="position:absolute"][style*="right"]:not([class*="print"]),
            div[style*="position: fixed"]:not([class*="print"]),
            div[style*="position:fixed"]:not([class*="print"]),
            /* Hide elements with visibility hidden */
            [style*="visibility: hidden"],
            [style*="visibility:hidden"] {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              height: 0 !important;
              width: 0 !important;
              overflow: hidden !important;
            }
            /* Additional specific rules for view toggle and UI controls */
            /* Hide any Box containing toggle buttons - use higher specificity */
            body [class*="MuiBox-root"].hide-on-print,
            html body [class*="MuiBox-root"].hide-on-print,
            body .hide-on-print,
            html body .hide-on-print {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              height: 0 !important;
              width: 0 !important;
              overflow: hidden !important;
            }
            /* Hide ToggleButtonGroup and related controls */
            body [class*="MuiToggleButtonGroup"],
            html body [class*="MuiToggleButtonGroup"],
            body [class*="MuiToggleButton"],
            html body [class*="MuiToggleButton"] {
              display: none !important;
              visibility: hidden !important;
            }
            /* Hide sticky positioned elements that are UI controls */
            body [style*="position: sticky"]:not([class*="print"]),
            body [style*="position:sticky"]:not([class*="print"]),
            html body [style*="position: sticky"]:not([class*="print"]),
            html body [style*="position:sticky"]:not([class*="print"]) {
              display: none !important;
              visibility: hidden !important;
            }
            /* Hide scrollbars */
            ::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              height: 0 !important;
            }
            * {
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
            }
            /* Ensure proper page breaks - each page rectangle = one A4 page */
            .prescription-page {
              page-break-after: always !important;
              page-break-inside: avoid !important;
              position: relative !important;
              width: ${widthOfA4Page}px !important;
              max-width: ${widthOfA4Page}px !important;
              min-width: ${widthOfA4Page}px !important;
              margin: 0 auto !important;
              padding: 0 !important;
              min-height: 0 !important;
              height: auto !important;
              display: block !important;
              /* Ensure page is visible */
              visibility: visible !important;
              opacity: 1 !important;
              /* Prevent overlapping */
              clear: both !important;
              float: none !important;
            }
            .prescription-page:last-of-type {
              page-break-after: auto !important;
            }
            [id^="a4-"] {
              page-break-inside: avoid !important;
              page-break-after: always !important;
              overflow: visible;
              position: relative !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              display: block !important;
              height: auto !important;
            }
            [id^="a4-"]:last-of-type {
              page-break-after: auto !important;
            }
            /* Ensure each page container takes exactly one A4 page */
            [id^="a4-"] > * {
              page-break-inside: avoid !important;
            }
            /* Ensure content doesn't break awkwardly */
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
              page-break-inside: avoid;
            }
            table {
              page-break-inside: avoid;
            }
            /* Clean up body for printing */
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
              background: white !important;
              /* Ensure content can be centered */
              display: block !important;
              /* Prevent scaling */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
            }
            /* Center page containers horizontally */
            [id^="a4-"],
            .prescription-page {
              margin-left: auto !important;
              margin-right: auto !important;
              display: block !important;
              /* Prevent scaling and ensure proper positioning */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
              left: auto !important;
              right: auto !important;
            }
            /* Ensure pages start from top and break correctly */
            [id^="a4-"] {
              margin-top: 0 !important;
              padding-top: 0 !important;
              margin-bottom: 0 !important;
              padding-bottom: 0 !important;
              position: relative !important;
              top: 0 !important;
              left: 0 !important;
              page-break-inside: avoid;
            }
            .A4PageContainer {
              margin-top: 0 !important;
              padding-top: 0 !important;
              margin-bottom: 0 !important;
              padding-bottom: 0 !important;
              margin-left: auto !important;
              margin-right: auto !important;
              position: relative !important;
              top: 0 !important;
              left: 0 !important;
              display: block !important;
            }
            /* Ensure first page starts from absolute top */
            [id^="a4-"]:first-of-type {
              margin-top: 0 !important;
              padding-top: 0 !important;
              page-break-before: auto !important;
            }
            /* Reset any top padding/margin on print margin wrapper for first page */
            [id^="a4-"]:first-of-type .print-margin-wrapper {
              padding-top: 0 !important;
              margin-top: 0 !important;
            }
            /* Ensure wrapper containers start from top */
            .A4WrapperContainer {
              margin-top: 0 !important;
              padding-top: 0 !important;
              position: static !important;
              height: auto !important;
              width: 100% !important;
              /* Prevent scaling */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
              /* Ensure all pages are visible */
              overflow: visible !important;
              /* Ensure all children are visible */
              display: block !important;
            }
            /* Ensure the Box container shows all pages */
            .A4WrapperContainer > * {
              display: block !important;
              visibility: visible !important;
            }
            /* Ensure measure wrapper starts from top */
            [class*="A4PageMeasureComponentWrapperContainer"] {
              margin-top: 0 !important;
              padding-top: 0 !important;
              position: relative !important;
              top: 0 !important;
            }
            /* Ensure all page content starts from top */
            [id^="a4-"] > * {
              margin-top: 0 !important;
            }
            [id^="a4-"] > *:first-child {
              margin-top: 0 !important;
              padding-top: 0 !important;
            }
            /* Ensure page breaks work correctly */
            [id^="a4-"]:not(:last-of-type) {
              page-break-after: always !important;
            }
            /* Prevent content from being positioned at bottom */
            [id^="a4-"] * {
              position: relative !important;
            }
            [id^="a4-"] [style*="position: absolute"] {
              position: relative !important;
            }
            /* Ensure all pages are visible and not scaled */
            .prescription-page,
            [id^="a4-"],
            .A4PageContainer,
            .A4PageMeasureComponentWrapperContainer {
              visibility: visible !important;
              display: block !important;
              opacity: 1 !important;
              /* Force exact dimensions */
              box-sizing: border-box !important;
              /* Ensure pages don't overlap */
              position: relative !important;
              clear: both !important;
              float: none !important;
            }
            /* Explicitly show all prescription pages */
            .prescription-page:not(.hide-page-in-print) {
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
              height: auto !important;
              min-height: 0 !important;
              /* Ensure pages are stacked, not overlapping */
              position: relative !important;
              top: auto !important;
              left: auto !important;
              right: auto !important;
              bottom: auto !important;
              margin-top: 0 !important;
              margin-bottom: 0 !important;
            }
            /* Force each page to start on a new page */
            .prescription-page:not(:first-of-type) {
              page-break-before: always !important;
            }
            /* Prevent browser from scaling pages */
            body > * {
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
            }
            /* Ensure Box containers don't hide pages */
            [class*="MuiBox-root"] {
              display: block !important;
              position: static !important;
              overflow: visible !important;
              height: auto !important;
              max-height: none !important;
              width: 100% !important;
              /* Ensure all children are visible */
              visibility: visible !important;
            }
            /* Specifically target the Box containing prescription pages */
            .A4WrapperContainer > [class*="MuiBox-root"] {
              display: block !important;
              height: auto !important;
              max-height: none !important;
              overflow: visible !important;
              position: static !important;
            }
            /* Ensure all prescription pages are stacked vertically */
            .prescription-page + .prescription-page {
              margin-top: 0 !important;
              page-break-before: always !important;
            }
            /* Ensure PMPConsultationParent doesn't get scaled */
            .PMPConsultationParent {
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
              width: ${widthOfA4Page}px !important;
              max-width: ${widthOfA4Page}px !important;
              min-width: ${widthOfA4Page}px !important;
            }
            /* Only apply @page rules when prescription pages are present */
            /* Note: @page rules are global but should only affect prescription pages */
            @page {
              size: A4;
              orientation: portrait;
              margin: 0 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              /* Prevent browser from scaling content */
              width: 210mm;
              height: 297mm;
            }
            /* Only apply html/body rules when prescription pages are present */
            /* Scope to avoid affecting other pages like prescription templates */
            /* Only apply when .prescription-page or [id^="a4-"] exists */
            /* Note: :has() selector may not be supported in all browsers */
            html:has(.prescription-page),
            html:has([id^="a4-"]),
            body:has(.prescription-page),
            body:has([id^="a4-"]) {
              width: 100% !important;
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: visible !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              /* Prevent any scaling transformations */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
              /* CRITICAL: Ensure all content is visible, not cut off */
              contain: none !important;
              /* Only apply flex layout when prescription pages are present */
              /* Removed display: flex to avoid breaking other pages */
              /* Prescription pages will be centered via margin: 0 auto on the page containers */
            }
            /* Ensure PMPPrintWrapper doesn't cut off pages */
            .PMPPrintWrapper,
            [class*="PMPPrintWrapper"] {
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
              overflow: visible !important;
              display: block !important;
              /* CRITICAL: Ensure all pages are within viewport */
              position: static !important;
              /* Remove any constraints that might hide first page */
              contain: none !important;
            }
            /* Ensure the Box wrapper containing pages doesn't hide them */
            .PMPPrintWrapper > *,
            .PMPPrintWrapper [class*="MuiBox-root"],
            .PMPPrintWrapper [class*="MuiBox-root"] > * {
              height: auto !important;
              max-height: none !important;
              overflow: visible !important;
              display: block !important;
              position: static !important;
            }
            /* CRITICAL: Ensure Box wrapper allows pages to flow vertically */
            .PMPPrintWrapper [class*="MuiBox-root"],
            .PMPPrintWrapper > [class*="MuiBox-root"],
            .PMPPrintWrapper [class*="MuiBox-root"][class*="css-"],
            .PMPPrintWrapper > div[class*="MuiBox-root"] {
              display: block !important;
              position: static !important;
              width: 100% !important;
              max-width: none !important;
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
              overflow: visible !important;
              /* Ensure it doesn't constrain children */
              contain: none !important;
              /* Remove any flex properties that might cause issues */
              flex: none !important;
              flexDirection: column !important;
              /* Prevent any scaling */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
            }
            /* Ensure all prescription pages are visible and stacked */
            .prescription-page,
            .prescription-page * {
              visibility: visible !important;
              opacity: 1 !important;
            }
            /* Critical: Ensure pages are not hidden by parent containers and stack vertically */
            .PMPPrintWrapper .prescription-page,
            .PMPPrintWrapper [class*="MuiBox-root"] .prescription-page {
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
              /* CRITICAL: Set explicit height and width to prevent overlap */
              height: ${printPageHeight}px !important;
              min-height: ${printPageHeight}px !important;
              max-height: ${printPageHeight}px !important;
              width: ${widthOfA4Page}px !important;
              max-width: ${widthOfA4Page}px !important;
              min-width: ${widthOfA4Page}px !important;
              position: static !important;
              page-break-after: always !important;
              /* Ensure pages flow naturally in document */
              clear: both !important;
              float: none !important;
              margin: 0 auto !important;
            }
            .PMPPrintWrapper .prescription-page:last-of-type {
              page-break-after: auto !important;
            }
            /* Ensure all content maintains physical size at higher DPIs */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            /* Ensure PMPConsultationParent maintains A4 aspect ratio in print */
            /* A4 aspect ratio: 11.69" / 8.27" = 1.414 (height/width) */
            /* For 96 DPI: 1123px / 794px = 1.414 */
            /* Use maximum specificity to override styled component rules */
            body .PMPConsultationParent,
            body [class*="PMPConsultationParent"],
            html body .PMPConsultationParent,
            html body [class*="PMPConsultationParent"] {
              /* Force exact A4 dimensions for border rectangle to match A4 aspect ratio */
              /* Border is included in dimensions (box-sizing: border-box) */
              /* A4 dimensions are always 8.27" × 11.69" regardless of DPI */
              /* Use pixels with correct DPI calculation to ensure proper size */
              /* At higher DPIs, use pixel dimensions from constants to maintain size */
              width: ${widthOfA4Page}px !important;
              max-width: ${widthOfA4Page}px !important;
              min-width: ${widthOfA4Page}px !important;
              height: ${printPageHeight}px !important;
              min-height: ${printPageHeight}px !important;
              max-height: ${printPageHeight}px !important;
              /* Center the rectangle horizontally on the page */
              margin: 0 auto !important;
              /* Ensure it's a block element for centering to work */
              display: block !important;
              /* Prevent any transforms or scaling */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              /* Ensure content doesn't get scaled by browser */
              image-rendering: -webkit-optimize-contrast !important;
              image-rendering: crisp-edges !important;
              /* Ensure proper positioning - start from top, centered horizontally */
              position: relative !important;
              left: auto !important;
              right: auto !important;
              top: 0 !important;
              margin: 0 !important;
              overflow: visible !important;
              box-sizing: border-box !important;
              /* Override any flex properties that might affect sizing */
              flex-grow: 0 !important;
              flex-shrink: 0 !important;
              flex-basis: auto !important;
              /* Ensure content fills the full height using flexbox */
              display: flex !important;
              flex-direction: column !important;
              /* Ensure the container itself takes full height */
              position: relative !important;
            }
            /* Ensure the inner content container fills the full height */
            body .PMPConsultationParent > *,
            body [class*="PMPConsultationParent"] > * {
              /* Allow flex children to grow to fill available space */
              flex-shrink: 0 !important;
            }
            /* Make the body content area grow to fill remaining space */
            body .PMPConsultationParent > div[style*="flex"],
            body [class*="PMPConsultationParent"] > div[style*="flex"] {
              flex: 1 1 auto !important;
              min-height: 0 !important;
            }
            /* Ensure body content container fills remaining height */
            /* This is the container that holds PrescriptionPrintableBody */
            body .PMPConsultationParent [class*="PmpConsultationBodyContentContainer"],
            body [class*="PMPConsultationParent"] [class*="PmpConsultationBodyContentContainer"] {
              flex: 1 1 auto !important;
              min-height: 0 !important;
              display: flex !important;
              flex-direction: column !important;
            }
            /* Ensure body content container fills remaining height */
            body .PMPConsultationParent [class*="PmpConsultationContentContainer"],
            body [class*="PMPConsultationParent"] [class*="PmpConsultationContentContainer"] {
              flex: 1 1 auto !important;
              min-height: 0 !important;
            }
            /* Ensure last page also fills full A4 height - add empty space if needed */
            body .PMPConsultationParent:last-of-type,
            body [class*="PMPConsultationParent"]:last-of-type,
            .prescription-page:last-of-type .PMPConsultationParent,
            .prescription-page:last-of-type [class*="PMPConsultationParent"] {
              /* Force full A4 height even if content is shorter */
              height: ${printPageHeight}px !important;
              min-height: ${printPageHeight}px !important;
              max-height: ${printPageHeight}px !important;
            }
            /* Ensure body content on last page expands to fill remaining space */
            body .PMPConsultationParent:last-of-type [class*="PmpConsultationBodyContentContainer"],
            body [class*="PMPConsultationParent"]:last-of-type [class*="PmpConsultationBodyContentContainer"],
            .prescription-page:last-of-type .PMPConsultationParent [class*="PmpConsultationBodyContentContainer"] {
              /* Expand to fill all available space on last page */
              flex: 1 1 auto !important;
              min-height: 0 !important;
            }
            [class*="MuiBox-root"] {
              height: auto !important;
              min-height: 0 !important;
              max-height: none !important;
              overflow: visible !important;
            }
            /* Force each page container to be exactly one A4 page */
            /* Each prescription-page div should map to exactly one printed A4 page */
            .prescription-page,
            div.prescription-page,
            .prescription-page.preview-page-container {
              page-break-after: always !important;
              page-break-inside: avoid !important;
              page-break-before: auto !important;
              display: block !important;
              /* CRITICAL: Use static positioning to ensure normal document flow */
              position: static !important;
              /* CRITICAL: Force correct width - override any inline styles */
              width: ${widthOfA4Page}px !important;
              max-width: ${widthOfA4Page}px !important;
              min-width: ${widthOfA4Page}px !important;
              margin: 0 auto !important;
              margin-bottom: 0 !important;
              padding: 0 !important;
              /* CRITICAL: Set explicit height to ensure pages don't overlap */
              height: ${printPageHeight}px !important;
              min-height: ${printPageHeight}px !important;
              max-height: ${printPageHeight}px !important;
              overflow: visible !important;
              /* Override any inline styles */
              box-sizing: border-box !important;
              /* Prevent scaling */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
              /* Ensure pages are stacked vertically, not overlapping */
              clear: both !important;
              float: none !important;
              /* Remove any absolute positioning */
              top: auto !important;
              left: auto !important;
              right: auto !important;
              bottom: auto !important;
              /* Ensure visibility */
              visibility: visible !important;
              opacity: 1 !important;
              /* Force each page to take full vertical space */
              box-sizing: border-box !important;
            }
            .prescription-page:first-of-type {
              page-break-before: auto !important;
              /* CRITICAL: Ensure first page is visible and not cut off */
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
              height: ${printPageHeight}px !important;
              min-height: ${printPageHeight}px !important;
              max-height: ${printPageHeight}px !important;
            }
            /* CRITICAL: Ensure ALL pages are visible in print, not just the last one */
            .prescription-page:nth-of-type(1),
            .prescription-page:nth-of-type(2),
            .prescription-page:nth-of-type(n) {
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
            .prescription-page:last-of-type {
              page-break-after: auto !important;
            }
            /* Each A4 container should be one page */
            [id^="a4-"] {
              page-break-after: always !important;
              page-break-inside: avoid !important;
              page-break-before: auto !important;
              display: block !important;
              /* CRITICAL: Use static positioning to ensure normal document flow */
              position: static !important;
              width: ${widthOfA4Page}px !important;
              max-width: ${widthOfA4Page}px !important;
              min-width: ${widthOfA4Page}px !important;
              margin: 0 auto !important;
              padding: 0 !important;
              /* Let content determine height, but ensure minimum A4 height */
              height: auto !important;
              min-height: ${printPageHeight}px !important;
              max-height: none !important;
              overflow: visible !important;
              /* Prevent scaling */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
              box-sizing: border-box !important;
            }
            /* Ensure A4PageContainer inside [id^="a4-"] maintains its width */
            [id^="a4-"] > .A4PageContainer {
              width: ${widthOfA4Page}px !important;
              min-width: ${widthOfA4Page}px !important;
              max-width: ${widthOfA4Page}px !important;
              margin: 0 auto !important;
              /* Prevent scaling */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
            }
            [id^="a4-"]:first-of-type {
              page-break-before: auto !important;
            }
            [id^="a4-"]:last-of-type {
              page-break-after: auto !important;
            }
            /* Ensure content flows naturally and doesn't get cut off */
            [id^="a4-"] > * {
              position: relative !important;
              height: auto !important;
              max-height: none !important;
            }
            /* Prevent any absolute positioning that might cause overlap */
            [id^="a4-"] [style*="position: absolute"],
            [id^="a4-"] [style*="position:absolute"] {
              position: relative !important;
            }
            /* Hide page break indicators in print */
            .page-break-indicator {
              display: none !important;
            }
            .preview-page-container {
              border: 1px solid rgba(0, 0, 0, 0.15) !important;
              border-radius: 8px !important;
              margin-bottom: 24px !important;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
              background: #fff !important;
              position: relative !important;
              overflow: visible !important;
              /* Ensure proper sizing in print */
              width: ${widthOfA4Page}px !important;
              max-width: ${widthOfA4Page}px !important;
              min-width: ${widthOfA4Page}px !important;
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
            }
            /* Ensure parent containers allow overflow */
            [id^="a4-"],
            .A4PageContainer,
            .A4PageMeasureComponentWrapperContainer {
              overflow: visible !important;
              /* Prevent scaling */
              transform: none !important;
              -webkit-transform: none !important;
              zoom: 1 !important;
              scale: 1 !important;
            }
            /* Ensure A4PageMeasureComponentWrapperContainer has correct width */
            .A4PageMeasureComponentWrapperContainer {
              width: ${widthOfA4Page}px !important;
              max-width: ${widthOfA4Page}px !important;
              min-width: ${widthOfA4Page}px !important;
            }
            .preview-page-container {
              border: none !important;
              border-radius: 0 !important;
              margin-bottom: 0 !important;
              box-shadow: none !important;
            }
            .page-break-indicator {
              display: none !important;
            }
            ${isSafari ? `
            /* Safari-only: gently zoom the printed page below 100% so a full-height A4 page
               fits within Safari's slightly-tighter printable area instead of spilling onto a
               trailing sheet. Mirrors the manual "Scale: 99%" fix. The two-class selector +
               last position overrides the many zoom:1 !important rules above, and zooming the
               .prescription-page (the page box) scales container + content together so full
               pages keep fitting. Chrome is unaffected (gated by isSafari). */
            html body .prescription-page,
            html body .prescription-page.preview-page-container {
              zoom: 0.99 !important;
            }
            ` : ''}
          }
        `}
      </style>
    <PMPPrintWrapper showInUI={showInUI}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, md: 2 },
          mb: 4,
          mx: { xs: 1, sm: 2, md: 3 },
          px: { xs: 1, sm: 0 },
        }}
        className="hide-on-print-only"
      >
        <PMPSectionHeading
          sx={{
            margin: 0,
            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
            mb: { xs: 1, md: 0 },
          }}
        >
          View Prescription
        </PMPSectionHeading>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1.5, sm: 2 },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {/* B&W / Colored Toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'space-between', sm: 'flex-start' },
              gap: 1,
              px: { xs: 1, sm: 0 },
              py: { xs: 0.5, sm: 0 },
              borderRadius: { xs: '8px', sm: 0 },
              backgroundColor: { xs: 'rgba(0, 0, 0, 0.02)', sm: 'transparent' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                color: 'text.secondary',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              B&W
            </Typography>
            <Switch
              checked={colorMode === 'colored'}
              onChange={(e) => handleColorModeChange(e.target.checked ? 'colored' : 'bw')}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4338ca',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4338ca',
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                color: 'text.secondary',
                fontWeight: { xs: 500, sm: 400 },
              }}
            >
              Colored
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 1, sm: 2 },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: { xs: '100%', sm: '200px' },
              }}
              className="hide-on-print-only"
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                PDF Scale:
              </Typography>
              <select
                value={printSettings?.canvasScale || 1.5}
                onChange={(e) => {
                  setPrintSettings((prev) => ({
                    ...(prev || {}),
                    canvasScale: parseFloat(e.target.value),
                  }))
                }}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '0.875rem',
                  minWidth: '120px',
                  flex: 1,
                }}
              >
                <option value={0.5}>0.5x (Fastest)</option>
                <option value={0.75}>0.75x (Fast)</option>
                <option value={1.0}>1.0x (Standard)</option>
                <option value={1.2}>1.2x (Good)</option>
                <option value={1.5}>1.5x (High - Default)</option>
                <option value={2.0}>2.0x (Highest)</option>
                <option value={2.5}>2.5x (Ultra)</option>
                <option value={3.0}>3.0x (Maximum)</option>
              </select>
            </Box>
            {/* PageSelectionMenu (Print All) hidden per user request */}
            {/* Paper Rx Template Preview Button - Disabled for now */}
            <Button
              variant="outlined"
              startIcon={<MenuBook />}
              disabled={true}
              sx={{
                padding: { xs: '6px 12px', sm: '8px 16px' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none',
                borderColor: 'rgba(67, 56, 202, 0.3)',
                color: 'rgba(67, 56, 202, 0.7)',
                backgroundColor: 'rgba(67, 56, 202, 0.04)',
                '&:hover': {
                  borderColor: 'rgba(67, 56, 202, 0.5)',
                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                  color: 'rgba(67, 56, 202, 0.9)',
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
              }}
              className="hide-on-print-only"
            >
              Paper Rx Template
            </Button>
          </Box>
        </Box>
      </Box>
      {!printSettings?.useOldPrintMethod && (
        <div className="mr-2">
          <div className="mx-3 hide-on-print-only" style={{ flex: 1 }}>
            <A4PrintSettings
              printSettings={printSettings}
              setPrintSettings={setPrintSettings}
              hideMarginSelection
              debug={DEBUG_MODE}
              clinicBrandId={bookedEventInfo?.clinicBrandId}
            />
          </div>
        </div>
      )}
      <div className={isPrintingPastHistoryReports ? 'hide-on-print' : ''}>
        <PrescriptionViewComponent
          showInUI={showInUI}
          debugMode={DEBUG_MODE}
          prescriptionData={prescriptionData}
          setPrescriptionData={setPrescriptionData}
          selectedObservations={selectedObservations}
          selectedComplaints={selectedComplaints}
          selectedDiagnosis={selectedDiagnosis}
          selectedAdvice={selectedAdvice}
          selectedInstructions={selectedInstructions}
          selectedTherapies={selectedTherapies}
          selectedAssessments={selectedAssessments}
          finalRequestedTestsBy={finalRequestedTestsBy}
          referredToDoctorName={referredToDoctorName}
          nextVisitInputType={nextVisitInputType}
          nextVisitInputValue={nextVisitInputValue}
          prescriptionViewSettings={prescriptionViewSettings}
          jointHomunculusData={jointHomunculusData}
          printSettings={printSettings}
          printType={printType}
          emrSections={emrSections}
          emrSectionsToShowInOrder={emrSectionsToShowInOrder}
          drugsAdvice={drugsAdvice}
          honorPropValue={honorPropValue}
          selectedInvestigations={selectedInvestigations}
          setPropValue={setPropValue}
          setValue={setValue}
          suggestedBookingTherapy={suggestedBookingTherapy}
          useOldUI={printSettings?.useOldPrintMethod}
          vital={allVitalsOfThisPatient?.find(
            (item) => item?.bookedEventId === bookedEventInfo?.id,
          )}
          value={value}
          prescriptionTemplateData={printSettings?.selectedPrescriptionTemplate}
          refreshDimensionsDependencies={[printSettings, prescriptionViewSettings]}
          patientHistoryNotes={patientHistoryNotes}
          freeFormRichText={freeFormRichText}
          onTotalPagesChange={setTotalPages}
          colorMode={colorMode}
        />
      </div>
    </PMPPrintWrapper>
    </>
  )
}

